<script setup lang="ts">
import { useForm } from 'vee-validate'
import { boolean, object, string, ref as reference } from 'yup'

defineEmits(['close'])

const { t, locale } = useI18n()
const uiStore = useUIStore()
const { handleRegistration } = useRegisteredCustomer()
const { accountUrl } = useSiteUrl()
const preferencesStore = usePreferencesStore()
const isLoading = ref(false)
const isShowAlert = ref(false)
const isShowConfirmation = ref(false)
const SECTION = 'validation_messages'
const tr = (key: string, section = SECTION) => t(`${section}.${key}`)

const validationSchema = object({
  email: string().email(tr('email')).required(tr('requiredEmail')),
  firstName: string().required(tr('requiredFirstName')).matches(REGEX_ACCEPTED_CHARACTERS, tr('firstName')),
  lastName: string().required(tr('requiredLastName')).matches(REGEX_ACCEPTED_CHARACTERS, tr('lastName')),
  /* phoneMobile: string().required(tr('requiredMobilePhone')).matches(REGEX_PHONE, tr('phone')), */ // TODO: mobile # validation rules
  password: string().required(tr('requiredPassword')).matches(REGEX_PASSWORD, tr('password')),
  confirmPassword: string()
    .required(tr('requiredConfirmPassword'))
    .oneOf([reference('password')], tr('confirmPassword')),
  dmy: object({
    month: string().required(tr('requiredBirthdayMonth')),
  }),
  termsConditions: boolean().required().isTrue(tr('termsConditions')),
})

const { errors, meta, values, defineField, handleSubmit, validateField } = useForm({
  validationSchema,
  initialValues: {
    email: '',
    firstName: '',
    lastName: '',
    // phoneMobile: '',
    password: '',
    confirmPassword: '',
    termsConditions: false,
    dmy: {
      day: '',
      month: '',
      year: '',
    },
  },
})

const [email, emailProps] = defineField(`email`, blurOnlyValidation)
const [firstName, firstNameProps] = defineField('firstName', blurOnlyValidation)
const [lastName, lastNameProps] = defineField('lastName', blurOnlyValidation)
// const [phoneMobile, mobileProps] = defineField('phoneMobile', blurOnlyValidation)
const [password, passwordProps] = defineField('password', blurOnlyValidation)
const [confirmPassword, confirmPasswordProps] = defineField('confirmPassword', blurOnlyValidation)
const [termsConditions, termsConditionsProps] = defineField('termsConditions', blurOnlyValidation)

type RegisterFieldName = 'email' | 'firstName' | 'lastName' | 'password' | 'confirmPassword' | 'termsConditions'
const { scrollToInvalidField, getBindFieldOptions, attempt, bumpAttempt } =
  createFormValidationHelpers<RegisterFieldName>({
    formSelector: '#signUp',
    getFieldErrors: () => errors.value,
    validateField,
  })

const formattedBirthday = computed(() => formatBirthMonth(values.dmy.month))

const onSubmit = handleSubmit(
  async (values) => {
    bumpAttempt()
    isLoading.value = true
    trackMyAccountEvent('custom.form.click', { label: 'Sign up', group: 'register' }, locale.value)

    try {
      const success = await handleRegistration({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
        birthday: formattedBirthday.value,
      })

      if (!success) {
        throw new Error('handleRegistration failed')
      }

      isShowConfirmation.value = true
    } catch (e) {
      isShowAlert.value = true
      await nextTick()
      scrollToError(document.querySelector('.register__error-message'))
      console.error('RegisterForm', e)
    } finally {
      isLoading.value = false
    }
  },
  ({ errors: submitErrors }) => {
    bumpAttempt()
    scrollToInvalidField(Object.keys(submitErrors).at(-1))
  },
)

watch(
  () => [uiStore.isShowSignUpModal, uiStore.loginEmail] as const,
  async ([isOpen, loginEmail]) => {
    if (!isOpen) return

    email.value = loginEmail ?? ''
    if (!email.value) return

    await nextTick()
    await validateField('email')
  },
  { immediate: true },
)
</script>

<template>
  <section class="register" ref="registerFormTop">
    <div v-if="isShowConfirmation" class="register__account-confirmation" data-testid="signup-confirmation">
      <!-- @vue-skip -->
      <RichTextPanel v-bind="{ ...preferencesStore?.site?.brillianceConfirmationPanel }" skip-container />
      <Button
        v-if="uiStore.isShowSignUpModal"
        variant="primary"
        @click="$emit('close')"
        class="register__account-confirmation__cta"
        data-testid="signup-continue"
      >
        {{ $t('my_account.account_confirmation.continue') }}
      </Button>
      <Anchor
        v-else
        class="account-confirmation__cta"
        data-testid="signup-continue"
        variant="primary"
        :text="$t('my_account.account_confirmation.continue')"
        :url="'/jewellery/we-love/new-in'"
      />
    </div>
    <div v-else>
      <LoadingSpinner v-if="isLoading" />
      <div class="register__header">
        <button :aria-label="$t('aria_label.close_dialog')" @click="$emit('close')">
          <svg-icon name="mhj:arrow-back" />
        </button>
        <h2 class="register__title">{{ $t('my_account.register.title') }}</h2>
      </div>

      <Transition name="expand">
        <AlertBar
          v-if="isShowAlert"
          variant="error"
          :icon-height="42"
          :icon-width="42"
          @close="isShowAlert = false"
          class="register__error-message"
        >
          <div>
            {{ $t('my_account.register.alert_bar_text') }}
            <a :href="accountUrl" data-testid="signup-signin-link">{{ $t('my_account.register.signin_link') }}.</a>
          </div>
        </AlertBar>
      </Transition>
      <p class="register__section-description">
        {{ $t('my_account.register.required_field') }}
      </p>

      <form id="signUp" @submit="onSubmit" novalidate>
        <Input
          v-model="email"
          v-bind="bindFieldProps(emailProps, getBindFieldOptions('email'))"
          :error="errors.email"
          :label="$t('checkout.shipping.inputs.email.label')"
          type="email"
          name="email"
          input-test-id="signup-email"
          required
        />

        <div class="register__name-row">
          <Input
            v-model="firstName"
            v-bind="bindFieldProps(firstNameProps, getBindFieldOptions('firstName'))"
            :error="errors.firstName"
            :label="$t('my_account.register.inputs.first_name.label')"
            name="firstName"
            input-test-id="signup-first-name"
            max-length="50"
            required
          />

          <Input
            v-model="lastName"
            v-bind="bindFieldProps(lastNameProps, getBindFieldOptions('lastName'))"
            :error="errors.lastName"
            :label="$t('my_account.register.inputs.last_name.label')"
            name="lastName"
            input-test-id="signup-last-name"
            max-length="50"
            required
          />
        </div>
        <!--
        <Input
          v-model="phoneMobile"
          v-bind="mobileProps"
          :error="errors.phoneMobile"
          :label="$t('my_account.register.inputs.phone_mobile.label')"
          name="mobile"
          required
        /> -->

        <Input
          v-model="password"
          v-bind="bindFieldProps(passwordProps, getBindFieldOptions('password'))"
          :error="errors.password"
          :label="$t('my_account.register.inputs.password.label')"
          :hint="
            errors.password !== '' && errors.password === $t('my_account.register.password_hint_text')
              ? ''
              : $t('my_account.register.password_hint_text')
          "
          name="password"
          input-test-id="signup-password"
          max-length="20"
          type="password"
          required
        />

        <Input
          v-model="confirmPassword"
          v-bind="bindFieldProps(confirmPasswordProps, getBindFieldOptions('confirmPassword'))"
          :error="errors.confirmPassword"
          name="confirmPassword"
          input-test-id="signup-confirm-password"
          :label="$t('my_account.register.inputs.confirm_password.label')"
          max-length="20"
          type="password"
          required
        />

        <div class="register__section-header">
          <h2 class="register__section-title">{{ $t('my_account.register.my_birthday_title') }}</h2>
          <p class="register__section-description">{{ $t('my_account.register.my_birthday_sub_title') }}</p>
        </div>

        <DatePicker
          month-only
          test-id-prefix="signup-birth"
          :label="$t('my_account.register.my_birthday_title')"
          :attempt="attempt"
        />

        <div class="register__terms-conditions">
          <Checkbox
            v-model="termsConditions"
            v-bind="bindFieldProps(termsConditionsProps, getBindFieldOptions('termsConditions'))"
            :error="errors.termsConditions"
            :label="$t('my_account.register.inputs.terms_conditions.label')"
            name="termsConditions"
            test-id="signup-terms"
            required
          />

          <i18n-t
            keypath="my_account.register.agreement_hint.text"
            scope="global"
            tag="div"
            class="register__terms-conditions-help-text"
          >
            <template #brillianceLink>
              <Anchor
                url="/brilliance-terms"
                :ga-options="{
                  group: 'register',
                  label: $t('my_account.register.agreement_hint.brilliance_link_text'),
                }"
              >
                {{ $t('my_account.register.agreement_hint.brilliance_link_text') }}
              </Anchor>
            </template>
            <template #websiteLink>
              <Anchor
                url="/terms"
                :ga-options="{
                  group: 'register',
                  label: $t('my_account.register.agreement_hint.website_link_text'),
                }"
              >
                {{ $t('my_account.register.agreement_hint.website_link_text') }}
              </Anchor>
            </template>
            <template #termsConditionsLink>
              <Anchor
                url="/general-terms"
                :ga-options="{
                  group: 'register',
                  label: $t('my_account.register.agreement_hint.terms_conditions_link_text'),
                }"
              >
                {{ $t('my_account.register.agreement_hint.terms_conditions_link_text') }}
              </Anchor>
            </template>
            <template #privacyPolicyLink>
              <Anchor
                url="/privacy-policy"
                :ga-options="{
                  group: 'register',
                  label: $t('my_account.register.agreement_hint.privacy_policy_link_text'),
                }"
              >
                {{ $t('my_account.register.agreement_hint.privacy_policy_link_text') }}
              </Anchor>
            </template>
          </i18n-t>
        </div>
        <Button type="submit" variant="primary" data-testid="signup-submit">{{
          $t('my_account.register.submit_button_text')
        }}</Button>
      </form>
    </div>
  </section>
</template>

<style lang="postcss">
.register {
  position: relative; /* required for the spinner */
  .register__header {
    display: flex;
    align-items: flex-start;
    gap: var(--space-40);
    margin-bottom: var(--space-60);

    button {
      padding: 0 var(--space-40) var(--space-40) 0;
    }
    svg {
      width: 1rem;
    }
  }
  .register__title {
    margin-bottom: 0;
  }
  .register__section-description {
    font-size: var(--text-small);
    color: var(--text-secondary);
    margin-top: var(--space-60);
  }

  .register__section-header {
    margin-top: var(--space-70);

    .register__section-title {
      margin-bottom: var(--space-30);
    }
    .register__section-description {
      margin-top: 0;
    }
  }

  .register__name-row {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: var(--space-50);

    @media (--tablet) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .date-picker {
    margin-bottom: var(--space-50);
  }

  .register__terms-conditions {
    margin-bottom: var(--space-70);

    .checkbox {
      padding-bottom: var(--space-20);
    }
  }

  .register__terms-conditions-help-text {
    font-size: var(--text-small);
    color: var(--text-secondary);
    margin-left: var(--space-70);
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    button {
      width: 100%;
    }
  }
  .register__account-confirmation {
    .register__account-confirmation__cta {
      margin-top: var(--space-70);
      width: 100%;
    }
  }
}
</style>
